import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { canView, getUserType } from "../permissions";
import API_BASE from "../config";

const Profile = () => {
    const navigate = useNavigate();
    const userType = getUserType();
    const allowAccess = canView(userType, "settings");

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [currentUser, setCurrentUser] = useState(null);

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showPassModal, setShowPassModal] = useState(false);
    const [passUser, setPassUser] = useState(null);

    // Add user form
    const [addForm, setAddForm] = useState({
        name: "", gmail: "", password: "", type: "", phone: "", project: "", city: "", address: "", client_id: "",
    });
    const [addSaving, setAddSaving] = useState(false);
    const [addMsg, setAddMsg] = useState({ text: "", type: "" });

    // Change password form
    const [passForm, setPassForm] = useState({ newPassword: "", confirmPassword: "" });
    const [passSaving, setPassSaving] = useState(false);
    const [passMsg, setPassMsg] = useState({ text: "", type: "" });

    useEffect(() => {
        // Get current user
        try {
            const session = JSON.parse(localStorage.getItem("user_session"));
            if (session) setCurrentUser(session);
        } catch (e) { }

        // Fetch all users
        setLoading(true);
        fetch(`${API_BASE}/api/allrows`, {
            method: "POST",
            headers: { Authorization: "1", "Content-Type": "application/json" },
            body: JSON.stringify({ token: "3232", table: "users" }),
        })
            .then((r) => r.json())
            .then((json) => {
                setLoading(false);
                if (Array.isArray(json.data)) setUsers(json.data);
                else setError("Failed to load users.");
            })
            .catch(() => { setLoading(false); setError("Error fetching users."); });
    }, []);

    const userTypes = [...new Set(users.map((u) => u.type).filter(Boolean))];

    let filtered = users.filter((u) =>
        Object.values(u).join(" ").toLowerCase().includes(search.toLowerCase())
    );
    if (typeFilter) filtered = filtered.filter((u) => u.type === typeFilter);

    // Find logged-in user details from API data
    const me = users.find((u) => u.id === currentUser?.id) || currentUser;

    const handleAddChange = (e) => setAddForm({ ...addForm, [e.target.name]: e.target.value });

    const handleAddUser = (e) => {
        e.preventDefault();
        setAddSaving(true); setAddMsg({ text: "", type: "" });
        // ============================================================
        // 🔧 DEMO API — Replace with your actual add user API
        // ============================================================
        fetch(`${API_BASE}/api/insert`, {
            method: "POST",
            headers: { Authorization: "1", "Content-Type": "application/json" },
            body: JSON.stringify({ token: "3232", table: "users", ...addForm }),
        })
            .then((r) => r.json())
            .then((json) => {
                setAddSaving(false);
                if (json.status == true) {
                    setAddMsg({ text: "User added successfully!", type: "ok" });
                    setAddForm({ name: "", gmail: "", password: "", type: "", phone: "", project: "", city: "", address: "", client_id: "" });
                    setTimeout(() => { setShowAddModal(false); window.location.reload(); }, 1200);
                } else setAddMsg({ text: json.message || "Failed to add user.", type: "err" });
            })
            .catch(() => { setAddSaving(false); setAddMsg({ text: "Error adding user.", type: "err" }); });
    };

    const handlePassChange = (e) => setPassForm({ ...passForm, [e.target.name]: e.target.value });

    const handleChangePassword = (e) => {
        e.preventDefault();
        if (passForm.newPassword !== passForm.confirmPassword) {
            setPassMsg({ text: "Passwords do not match!", type: "err" });
            return;
        }
        if (passForm.newPassword.length < 6) {
            setPassMsg({ text: "Password must be at least 6 characters.", type: "err" });
            return;
        }
        setPassSaving(true); setPassMsg({ text: "", type: "" });

        const formData = new URLSearchParams();
        formData.append("password", passForm.newPassword);

        fetch(`${API_BASE}/api/updatedatas`, {
            method: "POST",
            headers: { Authorization: "1", Table: "users", Id: String(passUser.id) },
            body: formData,
        })
            .then((r) => r.json())
            .then((json) => {
                setPassSaving(false);
                if (json.status == true) {
                    setPassMsg({ text: "Password changed successfully!", type: "ok" });
                    setTimeout(() => { setShowPassModal(false); setPassForm({ newPassword: "", confirmPassword: "" }); }, 1200);
                } else setPassMsg({ text: json.message || "Failed to change password.", type: "err" });
            })
            .catch(() => { setPassSaving(false); setPassMsg({ text: "Error changing password.", type: "err" }); });
    };

    // If user doesn't have access to settings, show access denied
    if (!allowAccess) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '60px' }}>🔒</div>
                <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#0f172a' }}>Access Restricted</h2>
                <p style={{ fontSize: '14px', color: '#64748b', maxWidth: '400px' }}>
                    You don't have permission to access the Settings page. Only administrators can manage users and settings.
                </p>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{ padding: '10px 24px', background: 'linear-gradient(135deg, #2563eb, #3b82f6)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                >
                    ← Back to Dashboard
                </button>
            </div>
        );
    }

    return (
        <>
            <style>{`
        .pr-page { display:flex; flex-direction:column; gap:24px; }

        /* Profile Card */
        .pr-profile-card { background:#fff; border:1px solid #e2e8f0; border-radius:20px; padding:32px; box-shadow:0 1px 3px rgba(0,0,0,.04); display:flex; align-items:center; gap:24px; position:relative; overflow:hidden; }
        .pr-profile-card::before { content:''; position:absolute; top:0; left:0; right:0; height:4px; background:linear-gradient(90deg,#2563eb,#6366f1,#8b5cf6); }
        .pr-avatar-lg { width:72px; height:72px; border-radius:16px; background:linear-gradient(135deg,#3b82f6,#6366f1); display:flex; align-items:center; justify-content:center; font-size:28px; font-weight:800; color:#fff; flex-shrink:0; }
        .pr-profile-info { flex:1; }
        .pr-profile-name { font-size:22px; font-weight:700; color:#0f172a; text-transform:capitalize; }
        .pr-profile-role { font-size:13px; color:#64748b; margin-top:2px; text-transform:capitalize; }
        .pr-profile-meta { display:flex; gap:20px; margin-top:12px; flex-wrap:wrap; }
        .pr-meta-item { display:flex; align-items:center; gap:6px; font-size:13px; color:#64748b; }
        .pr-meta-icon { font-size:15px; }
        .pr-profile-actions { display:flex; gap:10px; flex-shrink:0; }
        .pr-btn-pri { padding:10px 20px; background:linear-gradient(135deg,#2563eb,#3b82f6); color:#fff; border:none; border-radius:10px; font-size:13px; font-weight:600; cursor:pointer; font-family:'Inter',sans-serif; transition:all .2s; }
        .pr-btn-pri:hover { box-shadow:0 4px 14px rgba(37,99,235,.3); transform:translateY(-1px); }
        .pr-btn-sec { padding:10px 20px; background:#f8fafc; border:1px solid #e2e8f0; color:#334155; border-radius:10px; font-size:13px; font-weight:500; cursor:pointer; font-family:'Inter',sans-serif; transition:all .2s; }
        .pr-btn-sec:hover { background:#f1f5f9; border-color:#cbd5e1; }

        /* Users Section */
        .pr-section-header { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
        .pr-section-title { font-size:20px; font-weight:700; color:#0f172a; }
        .pr-section-count { font-size:13px; color:#64748b; margin-top:2px; }

        .pr-filters { background:#fff; padding:16px; border-radius:14px; border:1px solid #e2e8f0; box-shadow:0 1px 3px rgba(0,0,0,.04); display:flex; gap:12px; flex-wrap:wrap; align-items:center; }
        .pr-filters input,.pr-filters select { padding:10px 14px; border-radius:10px; border:1px solid #e2e8f0; outline:none; min-width:150px; font-size:13px; font-family:'Inter',sans-serif; color:#334155; background:#f8fafc; transition:all .2s; }
        .pr-filters input { flex:1; min-width:200px; }
        .pr-filters input:focus,.pr-filters select:focus { border-color:#93c5fd; box-shadow:0 0 0 3px rgba(59,130,246,.08); background:#fff; }

        /* Users Grid */
        .pr-users-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
        .pr-user-card { background:#fff; border:1px solid #e2e8f0; border-radius:16px; padding:20px; box-shadow:0 1px 3px rgba(0,0,0,.04); transition:all .25s; }
        .pr-user-card:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.07); }
        .pr-user-top { display:flex; align-items:center; gap:12px; margin-bottom:14px; }
        .pr-user-avatar { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:16px; font-weight:700; color:#fff; flex-shrink:0; }
        .pr-user-name { font-size:15px; font-weight:600; color:#0f172a; text-transform:capitalize; }
        .pr-user-type { font-size:12px; color:#64748b; text-transform:capitalize; }
        .pr-user-details { display:flex; flex-direction:column; gap:6px; margin-bottom:14px; }
        .pr-user-detail { display:flex; align-items:center; gap:8px; font-size:12px; color:#64748b; }
        .pr-user-detail-icon { font-size:14px; width:18px; text-align:center; }
        .pr-user-detail-val { font-weight:500; color:#334155; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .pr-user-actions { display:flex; gap:8px; padding-top:12px; border-top:1px solid #f1f5f9; }
        .pr-user-btn { flex:1; padding:7px 0; border-radius:8px; font-size:12px; font-weight:600; cursor:pointer; font-family:'Inter',sans-serif; transition:all .2s; text-align:center; border:1px solid #e2e8f0; background:#f8fafc; color:#334155; }
        .pr-user-btn:hover { background:#f1f5f9; }
        .pr-user-btn.pass { color:#d97706; border-color:#fcd34d; background:#fffbeb; }
        .pr-user-btn.pass:hover { background:#fef3c7; }
        .pr-user-btn.edit { color:#2563eb; border-color:#bfdbfe; background:#eff6ff; }
        .pr-user-btn.edit:hover { background:#dbeafe; }

        /* Role badges color */
        .pr-role-admin { background:linear-gradient(135deg,#dc2626,#ef4444); }
        .pr-role-accountant { background:linear-gradient(135deg,#059669,#10b981); }
        .pr-role-sales { background:linear-gradient(135deg,#2563eb,#3b82f6); }
        .pr-role-aftersales { background:linear-gradient(135deg,#7c3aed,#8b5cf6); }
        .pr-role-engineer { background:linear-gradient(135deg,#d97706,#f59e0b); }
        .pr-role-default { background:linear-gradient(135deg,#64748b,#94a3b8); }

        /* Modal */
        .pr-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.4); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; backdrop-filter:blur(4px); }
        .pr-modal { background:#fff; border-radius:20px; width:100%; max-width:580px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.15); }
        .pr-modal-header { padding:24px 28px 0; display:flex; justify-content:space-between; align-items:center; }
        .pr-modal-title { font-size:18px; font-weight:700; color:#0f172a; }
        .pr-modal-close { width:36px; height:36px; border-radius:10px; border:1px solid #e2e8f0; background:#f8fafc; color:#64748b; cursor:pointer; font-size:16px; display:flex; align-items:center; justify-content:center; transition:all .2s; }
        .pr-modal-close:hover { background:#f1f5f9; color:#0f172a; }
        .pr-modal-body { padding:24px 28px 28px; }
        .pr-modal-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:20px; }
        .pr-modal-field { display:flex; flex-direction:column; gap:6px; }
        .pr-modal-field.full { grid-column:1/-1; }
        .pr-modal-field label { font-size:12px; font-weight:600; color:#64748b; text-transform:uppercase; letter-spacing:.4px; }
        .pr-modal-field input,.pr-modal-field select { padding:11px 14px; border-radius:10px; border:1px solid #e2e8f0; font-size:14px; font-family:'Inter',sans-serif; color:#1e293b; background:#f8fafc; outline:none; transition:all .2s; }
        .pr-modal-field input:focus,.pr-modal-field select:focus { border-color:#93c5fd; box-shadow:0 0 0 3px rgba(59,130,246,.08); background:#fff; }
        .pr-modal-actions { display:flex; gap:12px; padding-top:16px; border-top:1px solid #f1f5f9; }
        .pr-modal-save { padding:11px 24px; background:linear-gradient(135deg,#2563eb,#3b82f6); color:#fff; border:none; border-radius:10px; font-size:14px; font-weight:600; cursor:pointer; font-family:'Inter',sans-serif; transition:all .2s; display:flex; align-items:center; gap:8px; }
        .pr-modal-save:hover:not(:disabled) { box-shadow:0 4px 14px rgba(37,99,235,.3); transform:translateY(-1px); }
        .pr-modal-save:disabled { opacity:.6; cursor:not-allowed; transform:none; }
        .pr-modal-cancel { padding:11px 20px; background:#f8fafc; color:#64748b; border:1px solid #e2e8f0; border-radius:10px; font-size:14px; font-weight:500; cursor:pointer; font-family:'Inter',sans-serif; }
        .pr-modal-cancel:hover { background:#f1f5f9; }
        .pr-m-spinner { width:16px; height:16px; border:2px solid rgba(255,255,255,.3); border-top-color:#fff; border-radius:50%; animation:pr-sp .6s linear infinite; }
        @keyframes pr-sp { to{transform:rotate(360deg)} }
        .pr-alert { padding:10px 14px; border-radius:8px; font-size:13px; font-weight:500; margin-bottom:16px; }
        .pr-alert-ok { background:#ecfdf5; color:#059669; border:1px solid #a7f3d0; }
        .pr-alert-err { background:#fef2f2; color:#dc2626; border:1px solid #fecaca; }

        /* Loading */
        .pr-loading { display:flex; flex-direction:column; align-items:center; padding:60px 20px; gap:14px; }
        .pr-spin { width:36px; height:36px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:pr-sp .7s linear infinite; }
        .pr-load-t { font-size:14px; color:#64748b; }
        .pr-empty { padding:40px; text-align:center; color:#94a3b8; font-size:14px; }

        /* Responsive */
        @media(max-width:1024px) { .pr-users-grid{grid-template-columns:repeat(2,1fr);} }
        @media(max-width:768px) {
          .pr-profile-card{flex-direction:column;text-align:center;}
          .pr-profile-meta{justify-content:center;}
          .pr-profile-actions{justify-content:center;}
          .pr-users-grid{grid-template-columns:1fr;}
          .pr-modal-grid{grid-template-columns:1fr;}
          .pr-filters{flex-direction:column;align-items:stretch;}
          .pr-filters input,.pr-filters select{min-width:100%;}
        }
        @media(max-width:480px) {
          .pr-profile-card{padding:20px;}
          .pr-avatar-lg{width:56px;height:56px;font-size:22px;}
        }
      `}</style>

            <div className="pr-page">
                {/* Admin Profile Card */}
                {me && (
                    <div className="pr-profile-card">
                        <div className="pr-avatar-lg">{(me.name || "A").charAt(0).toUpperCase()}</div>
                        <div className="pr-profile-info">
                            <div className="pr-profile-name">{me.name}</div>
                            <div className="pr-profile-role">{me.type || "Admin"}</div>
                            <div className="pr-profile-meta">
                                {(me.gmail || me.email) && <span className="pr-meta-item"><span className="pr-meta-icon">📧</span>{me.gmail || me.email}</span>}
                                {me.phone && <span className="pr-meta-item"><span className="pr-meta-icon">📱</span>{me.phone}</span>}
                                {me.project && <span className="pr-meta-item"><span className="pr-meta-icon">🏗️</span>Project: {me.project}</span>}
                                {me.city && <span className="pr-meta-item"><span className="pr-meta-icon">📍</span>{me.city}</span>}
                            </div>
                        </div>
                        <div className="pr-profile-actions">
                            <button className="pr-btn-sec" onClick={() => { setPassUser(me); setShowPassModal(true); setPassMsg({ text: "", type: "" }); setPassForm({ newPassword: "", confirmPassword: "" }); }}>
                                🔑 Change Password
                            </button>
                        </div>
                    </div>
                )}

                {/* Users Management Header */}
                <div className="pr-section-header">
                    <div>
                        <div className="pr-section-title">👥 User Management</div>
                        <div className="pr-section-count">{loading ? "Loading..." : `${filtered.length} users`}</div>
                    </div>
                    <button className="pr-btn-pri" onClick={() => { setShowAddModal(true); setAddMsg({ text: "", type: "" }); }}>
                        ➕ Add User
                    </button>
                </div>

                {/* Filters */}
                <div className="pr-filters">
                    <input placeholder="🔍 Search by name, email, role..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                        <option value="">All Roles</option>
                        {userTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>

                {error && <div className="pr-alert pr-alert-err">⚠️ {error}</div>}

                {loading ? (
                    <div className="pr-loading"><div className="pr-spin" /><div className="pr-load-t">Loading users...</div></div>
                ) : filtered.length === 0 ? (
                    <div className="pr-empty">No users found</div>
                ) : (
                    <div className="pr-users-grid">
                        {filtered.map((u) => {
                            const roleClass = u.type === "admin" ? "pr-role-admin" :
                                u.type === "Accountant" ? "pr-role-accountant" :
                                    u.type === "Sales Executive" ? "pr-role-sales" :
                                        u.type === "After Sales" ? "pr-role-aftersales" :
                                            u.type === "Project Engineer" ? "pr-role-engineer" : "pr-role-default";
                            return (
                                <div key={u.id} className="pr-user-card">
                                    <div className="pr-user-top">
                                        <div className={`pr-user-avatar ${roleClass}`}>{(u.name || "U").charAt(0).toUpperCase()}</div>
                                        <div>
                                            <div className="pr-user-name">{u.name}</div>
                                            <div className="pr-user-type">{u.type || "User"}</div>
                                        </div>
                                    </div>
                                    <div className="pr-user-details">
                                        {u.gmail && <div className="pr-user-detail"><span className="pr-user-detail-icon">📧</span><span className="pr-user-detail-val">{u.gmail}</span></div>}
                                        {u.phone && <div className="pr-user-detail"><span className="pr-user-detail-icon">📱</span><span className="pr-user-detail-val">{u.phone}</span></div>}
                                        {u.project && <div className="pr-user-detail"><span className="pr-user-detail-icon">🏗️</span><span className="pr-user-detail-val">Project: {u.project}</span></div>}
                                        {u.client_id && <div className="pr-user-detail"><span className="pr-user-detail-icon">🆔</span><span className="pr-user-detail-val">{u.client_id}</span></div>}
                                        {u.city && <div className="pr-user-detail"><span className="pr-user-detail-icon">📍</span><span className="pr-user-detail-val">{u.city}</span></div>}
                                    </div>
                                    <div className="pr-user-actions">
                                        <button className="pr-user-btn edit" onClick={() => navigate("/dashboard/settings/edit-user", { state: { user: u } })}>
                                            ✏️ Edit
                                        </button>
                                        <button className="pr-user-btn pass" onClick={() => { setPassUser(u); setShowPassModal(true); setPassMsg({ text: "", type: "" }); setPassForm({ newPassword: "", confirmPassword: "" }); }}>
                                            🔑 Password
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ===== ADD USER MODAL ===== */}
            {showAddModal && (
                <div className="pr-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
                    <div className="pr-modal">
                        <div className="pr-modal-header">
                            <div className="pr-modal-title">➕ Add New User</div>
                            <button className="pr-modal-close" onClick={() => setShowAddModal(false)}>✕</button>
                        </div>
                        <form className="pr-modal-body" onSubmit={handleAddUser}>
                            {addMsg.text && <div className={`pr-alert ${addMsg.type === "ok" ? "pr-alert-ok" : "pr-alert-err"}`}>{addMsg.type === "ok" ? "✅" : "⚠️"} {addMsg.text}</div>}
                            <div className="pr-modal-grid">
                                <div className="pr-modal-field"><label>Full Name *</label><input name="name" value={addForm.name} onChange={handleAddChange} required /></div>
                                <div className="pr-modal-field"><label>Email *</label><input name="gmail" type="email" value={addForm.gmail} onChange={handleAddChange} required /></div>
                                <div className="pr-modal-field"><label>Password *</label><input name="password" value={addForm.password} onChange={handleAddChange} required /></div>
                                <div className="pr-modal-field">
                                    <label>Role *</label>
                                    <select name="type" value={addForm.type} onChange={handleAddChange} required>
                                        <option value="">Select Role</option>
                                        <option value="admin">Admin</option>
                                        <option value="Accountant">Accountant</option>
                                        <option value="Sales Executive">Sales Executive</option>
                                        <option value="After Sales">After Sales</option>
                                        <option value="Project Engineer">Project Engineer</option>
                                    </select>
                                </div>
                                <div className="pr-modal-field"><label>Phone</label><input name="phone" value={addForm.phone} onChange={handleAddChange} /></div>
                                <div className="pr-modal-field"><label>Client ID</label><input name="client_id" value={addForm.client_id} onChange={handleAddChange} /></div>
                                <div className="pr-modal-field"><label>Project</label><input name="project" value={addForm.project} onChange={handleAddChange} placeholder="e.g. SEI, SHC" /></div>
                                <div className="pr-modal-field"><label>City</label><input name="city" value={addForm.city} onChange={handleAddChange} /></div>
                                <div className="pr-modal-field full"><label>Address</label><input name="address" value={addForm.address} onChange={handleAddChange} /></div>
                            </div>
                            <div className="pr-modal-actions">
                                <button type="submit" className="pr-modal-save" disabled={addSaving}>
                                    {addSaving && <span className="pr-m-spinner" />}
                                    {addSaving ? "Adding..." : "➕ Add User"}
                                </button>
                                <button type="button" className="pr-modal-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ===== CHANGE PASSWORD MODAL ===== */}
            {showPassModal && passUser && (
                <div className="pr-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowPassModal(false); }}>
                    <div className="pr-modal" style={{ maxWidth: 440 }}>
                        <div className="pr-modal-header">
                            <div className="pr-modal-title">🔑 Change Password</div>
                            <button className="pr-modal-close" onClick={() => setShowPassModal(false)}>✕</button>
                        </div>
                        <form className="pr-modal-body" onSubmit={handleChangePassword}>
                            {passMsg.text && <div className={`pr-alert ${passMsg.type === "ok" ? "pr-alert-ok" : "pr-alert-err"}`}>{passMsg.type === "ok" ? "✅" : "⚠️"} {passMsg.text}</div>}
                            <div style={{ padding: "12px 16px", background: "#f8fafc", borderRadius: 12, marginBottom: 20, border: "1px solid #f1f5f9" }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", textTransform: "capitalize" }}>{passUser.name}</div>
                                <div style={{ fontSize: 12, color: "#64748b" }}>{passUser.type || passUser.gmail || ""}</div>
                            </div>
                            <div className="pr-modal-grid" style={{ gridTemplateColumns: "1fr" }}>
                                <div className="pr-modal-field"><label>New Password</label><input name="newPassword" type="password" value={passForm.newPassword} onChange={handlePassChange} required minLength={6} /></div>
                                <div className="pr-modal-field"><label>Confirm Password</label><input name="confirmPassword" type="password" value={passForm.confirmPassword} onChange={handlePassChange} required /></div>
                            </div>
                            <div className="pr-modal-actions">
                                <button type="submit" className="pr-modal-save" disabled={passSaving}>
                                    {passSaving && <span className="pr-m-spinner" />}
                                    {passSaving ? "Saving..." : "🔑 Change Password"}
                                </button>
                                <button type="button" className="pr-modal-cancel" onClick={() => setShowPassModal(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default Profile;
